import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";

import { hardRedirect, isApiUnreachableError, shouldRetryQuery } from "@/lib/api/client";
import { INTERNAL_ERROR_PATH } from "@/lib/auth/auth-paths";
import { UserSettingsProvider } from "@/components/user-settings/user-settings-provider";
import { AuthProvider } from "@/stores/auth-store";
import { ServiceProvider } from "@/stores/service-store";

interface ProvidersProps {
  children: React.ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        // Unreachable-API errors are not retried (see the retry policy below),
        // so this fires on the first failure. The guard avoids a redirect loop
        // once we are already there. A failed background refetch of data that
        // is already on screen (polling) keeps the page: it still works and
        // the next refetch may succeed. Mutations are intentionally not
        // handled here, so forms keep their inline errors.
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (
              query.state.data === undefined &&
              isApiUnreachableError(error) &&
              window.location.pathname !== INTERNAL_ERROR_PATH
            ) {
              hardRedirect(INTERNAL_ERROR_PATH);
            }
          },
        }),
        defaultOptions: {
          queries: {
            retry: shouldRetryQuery,
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserSettingsProvider>
          <ServiceProvider>
            {children}
          </ServiceProvider>
        </UserSettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
